import { useTheme } from '@hooks'
import { IBalanceForecastDay } from '@root/query'
import React, { useId, useMemo, useState } from 'react'
import styled, { keyframes } from 'styled-components'
import { WHEN_STACKED } from '../../layout'

const VIEW_WIDTH = 400
const VIEW_HEIGHT = 80
const PLOT_TOP = 6
const PLOT_BOTTOM = 74

const toSmoothPath = (points: Array<{ x: number; y: number }>) => {
  if (points.length === 0) return ''
  if (points.length === 1) return `M ${points[0].x.toFixed(1)} ${points[0].y.toFixed(1)}`

  const parts = [`M ${points[0].x.toFixed(1)} ${points[0].y.toFixed(1)}`]
  for (let index = 0; index < points.length - 1; index += 1) {
    const current = points[index]
    const next = points[index + 1]
    const midX = (current.x + next.x) / 2
    const midY = (current.y + next.y) / 2
    parts.push(
      `Q ${current.x.toFixed(1)} ${current.y.toFixed(1)} ${midX.toFixed(1)} ${midY.toFixed(1)}`
    )
  }
  const last = points[points.length - 1]
  parts.push(`T ${last.x.toFixed(1)} ${last.y.toFixed(1)}`)
  return parts.join(' ')
}

export interface ScrubPoint {
  day: IBalanceForecastDay
  isForecast: boolean
}

interface Props {
  days: IBalanceForecastDay[]
  formatDate: (isoDate: string) => string
  onScrub: (point: ScrubPoint | null) => void
}

export const BalanceSparkline: React.FC<Props> = ({ days, formatDate, onScrub }) => {
  const theme = useTheme()
  const fillId = useId().replace(/:/g, '')
  const [scrubIndex, setScrubIndex] = useState<number | null>(null)

  const geometry = useMemo(() => {
    if (days.length < 2) return null

    const balances = days.map(day => day.balance)
    const low = Math.min(...balances)
    const high = Math.max(...balances)
    const span = high - low || 1

    const x = (index: number) => (index / (days.length - 1)) * VIEW_WIDTH
    const y = (balance: number) => PLOT_BOTTOM - ((balance - low) / span) * (PLOT_BOTTOM - PLOT_TOP)

    const coords = days.map((day, index) => ({ x: x(index), y: y(day.balance) }))
    const todayIndex = Math.max(
      0,
      days.findIndex(day => day.current)
    )
    const actualCoords = coords.slice(0, todayIndex + 1)
    const forecastCoords = coords.slice(todayIndex)
    const today = coords[todayIndex]

    return {
      x,
      y,
      todayIndex,
      todayLeft: `${(todayIndex / (days.length - 1)) * 100}%`,
      todayTop: `${(today.y / VIEW_HEIGHT) * 100}%`,
      actual: toSmoothPath(actualCoords),
      forecast: toSmoothPath(forecastCoords),
      area: [
        `M 0,${VIEW_HEIGHT}`,
        `L ${actualCoords[0].x.toFixed(1)},${VIEW_HEIGHT}`,
        `L ${actualCoords[0].x.toFixed(1)},${actualCoords[0].y.toFixed(1)}`,
        toSmoothPath(actualCoords).replace(/^M [^ ]+ [^ ]+ /, ''),
        `L ${today.x.toFixed(1)},${VIEW_HEIGHT}`,
        'Z'
      ].join(' ')
    }
  }, [days])

  if (!geometry) return null

  const report = (index: number | null) => {
    setScrubIndex(index)
    if (index === null) {
      onScrub(null)
      return
    }
    onScrub({ day: days[index], isForecast: index > geometry.todayIndex })
  }

  const handleMove = (event: React.MouseEvent<HTMLDivElement>) => {
    const bounds = event.currentTarget.getBoundingClientRect()
    const ratio = (event.clientX - bounds.left) / bounds.width
    const index = Math.max(0, Math.min(days.length - 1, Math.round(ratio * (days.length - 1))))
    if (index !== scrubIndex) report(index)
  }

  const isForecast = scrubIndex !== null && scrubIndex > geometry.todayIndex

  return (
    <Plot onMouseMove={handleMove} onMouseLeave={() => report(null)}>
      <svg
        viewBox={`0 0 ${VIEW_WIDTH} ${VIEW_HEIGHT}`}
        preserveAspectRatio="none"
        role="presentation"
      >
        <defs>
          <linearGradient id={fillId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={theme.luca.sky} stopOpacity={0.32} />
            <stop offset="100%" stopColor={theme.luca.sky} stopOpacity={0} />
          </linearGradient>
        </defs>
        <path d={geometry.area} fill={`url(#${fillId})`} />
        <path
          d={geometry.actual}
          fill="none"
          stroke={theme.luca.sky}
          strokeWidth={2.5}
          strokeLinecap="round"
          strokeLinejoin="round"
          vectorEffect="non-scaling-stroke"
        />
        <path
          d={geometry.forecast}
          fill="none"
          stroke={theme.colors.primary}
          strokeWidth={2.5}
          strokeDasharray="4 5"
          strokeLinecap="round"
          strokeLinejoin="round"
          vectorEffect="non-scaling-stroke"
        />
        <line
          x1={geometry.x(geometry.todayIndex)}
          y1={0}
          x2={geometry.x(geometry.todayIndex)}
          y2={VIEW_HEIGHT}
          stroke="rgba(255, 255, 255, 0.15)"
          strokeWidth={1}
          vectorEffect="non-scaling-stroke"
        />
        {scrubIndex !== null && (
          <line
            x1={geometry.x(scrubIndex)}
            y1={0}
            x2={geometry.x(scrubIndex)}
            y2={VIEW_HEIGHT}
            stroke="rgba(255, 255, 255, 0.5)"
            strokeWidth={1}
            vectorEffect="non-scaling-stroke"
          />
        )}
      </svg>
      <Knob
        style={{ left: geometry.todayLeft, top: geometry.todayTop }}
        $fill={theme.colors.menuBg}
        $stroke={theme.luca.sky}
        $size={8}
      />
      {scrubIndex !== null && (
        <Knob
          style={{
            left: `${(scrubIndex / (days.length - 1)) * 100}%`,
            top: `${(geometry.y(days[scrubIndex].balance) / VIEW_HEIGHT) * 100}%`
          }}
          $fill={theme.colors.neutralWhite}
          $stroke={isForecast ? theme.colors.primary : theme.luca.sky}
          $size={10}
        />
      )}
      {scrubIndex !== null && (
        <ScrubLabel
          style={{ left: `${(scrubIndex / (days.length - 1)) * 100}%` }}
          aria-hidden="true"
        >
          {formatDate(days[scrubIndex].date)}
        </ScrubLabel>
      )}
    </Plot>
  )
}

export const BalanceSparklineSkeleton: React.FC = () => {
  const theme = useTheme()
  const fillId = useId().replace(/:/g, '')
  const todayX = 220
  const todayY = 38
  const actual = `M 0 48 Q 40 46 80 44 T 160 40 T ${todayX} ${todayY}`
  const forecast = `M ${todayX} ${todayY} T 310 42 T 400 40`
  const area = `M 0 ${VIEW_HEIGHT} L 0 48 Q 40 46 80 44 T 160 40 T ${todayX} ${todayY} L ${todayX} ${VIEW_HEIGHT} Z`

  return (
    <Plot $loading aria-hidden="true">
      <svg
        viewBox={`0 0 ${VIEW_WIDTH} ${VIEW_HEIGHT}`}
        preserveAspectRatio="none"
        role="presentation"
      >
        <defs>
          <linearGradient id={fillId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={theme.luca.sky} stopOpacity={0.22} />
            <stop offset="100%" stopColor={theme.luca.sky} stopOpacity={0} />
          </linearGradient>
        </defs>
        <path d={area} fill={`url(#${fillId})`} />
        <path
          d={actual}
          fill="none"
          stroke={theme.luca.sky}
          strokeWidth={2.5}
          strokeLinecap="round"
          strokeLinejoin="round"
          vectorEffect="non-scaling-stroke"
          opacity={0.45}
        />
        <path
          d={forecast}
          fill="none"
          stroke={theme.colors.primary}
          strokeWidth={2.5}
          strokeDasharray="4 5"
          strokeLinecap="round"
          strokeLinejoin="round"
          vectorEffect="non-scaling-stroke"
          opacity={0.35}
        />
        <line
          x1={todayX}
          y1={0}
          x2={todayX}
          y2={VIEW_HEIGHT}
          stroke="rgba(255, 255, 255, 0.15)"
          strokeWidth={1}
          vectorEffect="non-scaling-stroke"
        />
      </svg>
      <Knob
        style={{ left: `${(todayX / VIEW_WIDTH) * 100}%`, top: `${(todayY / VIEW_HEIGHT) * 100}%` }}
        $fill={theme.colors.menuBg}
        $stroke={theme.luca.sky}
        $size={8}
      />
      <Sweep />
    </Plot>
  )
}

const Plot = styled.div<{ $loading?: boolean }>`
  position: relative;
  display: flex;
  flex: 1;
  min-height: 4.625rem;
  cursor: ${({ $loading }) => ($loading ? 'default' : 'crosshair')};

  ${WHEN_STACKED} {
    flex: 0 0 5.5rem;
    max-height: 7.5rem;
  }

  /* Absolute so the svg does not size to the path and overflow the axis. */
  svg {
    position: absolute;
    inset: 0;
    display: block;
    width: 100%;
    height: 100%;
    overflow: visible;
  }
`

const Knob = styled.span<{ $fill: string; $stroke: string; $size: number }>`
  position: absolute;
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  border-radius: 50%;
  background: ${({ $fill }) => $fill};
  border: 2.5px solid ${({ $stroke }) => $stroke};
  transform: translate(-50%, -50%);
  pointer-events: none;
  box-sizing: border-box;
`

const sweep = keyframes`
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
`

const Sweep = styled.span`
  position: absolute;
  inset: 0;
  overflow: hidden;
  pointer-events: none;
  border-radius: ${({ theme }) => theme.borderRadius.sm}rem;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    width: 55%;
    background: linear-gradient(
      90deg,
      rgba(255, 255, 255, 0) 0%,
      rgba(255, 255, 255, 0.06) 35%,
      rgba(255, 255, 255, 0.18) 50%,
      rgba(255, 255, 255, 0.06) 65%,
      rgba(255, 255, 255, 0) 100%
    );
    animation: ${sweep} 1.4s ease-in-out infinite;
  }
`

const ScrubLabel = styled.span`
  position: absolute;
  top: -0.375rem;
  transform: translateX(-50%);
  padding: 0.125rem ${({ theme }) => theme.spacing.sm}rem;
  border-radius: 999px;
  background: ${({ theme }) => theme.colors.neutralWhite};
  color: ${({ theme }) => theme.colors.menuBg};
  font-size: ${({ theme }) => theme.fontSize.px10}rem;
  font-weight: 700;
  white-space: nowrap;
  pointer-events: none;
  box-shadow: 0 1px 4px rgba(3, 30, 51, 0.25);
`
